/// <amd-module name="Build/Designer/PipelineVariablesPanel" />
import * as React from "react";
import ReactDOM = require("react-dom");

import { BuildDefinition, BuildDefinitionVariable } from "Build/Client/Contracts/Build";
import { CopyToClipboardButton } from "Build/Common/Library/Components/CopyToClipboardButton";

import * as CommonResources from "Build/Common/Library/Resources";
import { FwLinks } from "Build/Designer/Constants";
import { addNavigateAwayListener, removeNavigateAwayListener, showLoseChangesDialog } from "Build/Designer/LoseChangesDialog";
import { PipelineVariableEditPanel, PipelineVariableInfo } from "Build/Designer/PipelineVariableEditPanel";
import * as Resources from "Build/Designer/Resources";

import { ObservableArray } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { TitleSize } from "VSSUI/Header";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, IListRow, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { TextField, TextFieldStyle } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";

interface PipelineVariablesProps {
    variables: { [key: string] : BuildDefinitionVariable };
    errorMessage?: string;
    isSaving: boolean;
    onDismiss: () => void;
    onSave: (pipelineVariables: { [name: string]: BuildDefinitionVariable }) => void;
}

interface PipelineVariablesState {
    isEditPanelOpen: boolean;
    variableToEdit?: PipelineVariableInfo;
    isDirty: boolean;
    searchText: string;
}

export class PipelineVariablesPanel extends VssComponent<PipelineVariablesProps, PipelineVariablesState> {
    private _originalVariables: PipelineVariableInfo[];
    private _modifiedVariables: PipelineVariableInfo[];
    private _filteredVariables: ObservableArray<PipelineVariableInfo>;
    private _selection = new ListSelection(true);
    private _timerManagement: TimerManagement;
    private _throttledSearch: (searchString: string) => void;

    constructor(props: PipelineVariablesProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isDirty: false,
            isEditPanelOpen: false,
            searchText: ""
        };

        this._originalVariables = this._getExistingVariables();
        this._modifiedVariables = this._originalVariables.map(variable => ({ ...variable }));
        this._filteredVariables = new ObservableArray([...this._modifiedVariables]);

        this._timerManagement = new TimerManagement();
        this._throttledSearch = this._timerManagement.debounce((searchText: string) => this._updateFilteredList(searchText), 250);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        addNavigateAwayListener(this._onNavigateAway);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        removeNavigateAwayListener(this._onNavigateAway);
    }

    public render(): JSX.Element {
        const isDirty = this.state.isDirty;
        const titleText = Resources.VariablesPanelTitle;
        const hasVariables = this._modifiedVariables.length || this.state.variableToEdit;

        return (
            <CustomPanel
                ariaLabel={titleText}
                className={"pipeline-editor-variables-panel"}
                defaultActiveElement={hasVariables ? ".variables-search-input input" : ".empty-new-button"}
                onDismiss={this._onDismiss}
                escDismiss={!isDirty && !this.props.isSaving}
                lightDismiss={!isDirty && !this.props.isSaving}>
                <PanelHeader
                    titleProps={{ text: titleText, size: TitleSize.Large }}
                    onDismiss={this._onDismiss} />

                {hasVariables
                    ? <>
                        <PanelContent>
                            {this._renderBody()}
                        </PanelContent>
                        {this._renderFooter()}
                    </>
                    : this._renderEmptyScenario()
                }
            </CustomPanel>
        );
    }

    private _renderBody(): JSX.Element {
        return (
            <div className="variables-content-body flex-column flex-grow">
                {
                    this.props.errorMessage &&
                    <MessageCard
                        className="error-message"
                        severity={MessageCardSeverity.Error}>
                        {this.props.errorMessage}
                    </MessageCard>
                }
                <div className="variables-controls-container flex-row">
                    <TextField
                        value={this.state.searchText}
                        className={"variables-search-input"}
                        containerClassName={"flex-grow"}
                        placeholder={Resources.VariablesPanelSearch}
                        ariaLabel={Resources.VariablesPanelSearch}
                        prefixIconProps={{ iconName: "Search" }}
                        onChange={this._onSearchTextChanged}
                        style={TextFieldStyle.inline}
                    />
                    <Button
                        iconProps={{ iconName: "Add" }}
                        onClick={this._newVariable}
                        ariaLabel={Resources.VariablesPanelNewVariable}
                        tooltipProps={{ text: Resources.VariablesPanelNewVariable }}
                        className={"variable-commands"} />
                </div>

                <ScrollableList
                    itemProvider={this._filteredVariables}
                    renderRow={this._renderRow}
                    selection={this._selection}
                    width="100%"
                    singleClickActivation={true}
                    onActivate={this._rowClicked} />

                {this.state.isEditPanelOpen && this.state.variableToEdit &&
                    <PipelineVariableEditPanel
                        allVariables={this._modifiedVariables}
                        variable={this.state.variableToEdit}
                        onDismiss={this._editPanelDismissed}
                        onUpdate={this._updateVariable} />
                }
            </div>
        );
    }

    private _renderRow = (
        index: number,
        item: PipelineVariableInfo,
        details: IListItemDetails<PipelineVariableInfo>,
        key?: string
    ): JSX.Element => {
        const icon = item.isDeleted ? "Delete" : item.isSecret ? "Lock" : "Variable";
        const value = item.isDeleted ? "Deleted" : item.isSecret ? CommonResources.VariableSecretMask : "= " + item.value;
        const modifiedClass = item.isDeleted ? "is-deleted" : item.isDirty ? "font-weight-semibold" : "";
        const deleteCommandText = item.isDeleted ? Resources.VariablesPanelCommandUndoDelete : Resources.VariablesPanelCommandDelete;

        return (
            <ListItem key={key || "list-item" + index} index={index} details={details} className={item.isDeleted ? "deleted-variable-row" : ""} >
                <div className="variable-row full-size">
                    <Icon iconName={icon} size={IconSize.medium} className="variable-icon" />

                    <div className={css("variable-content flex-column h-scroll-hidden flex-grow", modifiedClass)}>
                        <Tooltip overflowOnly={true}>
                            <span className={"text-ellipsis primary-text"}>{item.name}</span>
                        </Tooltip>
                        <Tooltip overflowOnly={true}>
                            <span className="font-size text-ellipsis secondary-text">{value}</span>
                        </Tooltip>
                    </div>

                    <ButtonGroup className="flex-noshrink justify-end bolt-list-cell-content-reveal" >
                        {!item.isDeleted &&
                            <CopyToClipboardButton
                                onClickStopPropagation={true}
                                textToCopy={() => "$(" + item.name + ")"} />
                        }
                        <Button
                            key={"delete"}
                            ariaLabel={deleteCommandText}
                            iconProps={{ iconName: item.isDeleted ? "Undo" : "Delete" }}
                            tooltipProps={{ text: deleteCommandText }}
                            subtle={true}
                            onClick={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                this._setDeleted(item, !item.isDeleted);
                                event && event.stopPropagation();
                            }} />
                    </ButtonGroup>
                </div>
            </ListItem>
        );
    };

    private _renderFooter(): JSX.Element {
        const isDirty = this.state.isDirty;

        return (
            <PanelFooter
                className="flex-row flex-center"
                showSeparator={true}>
                <Link
                    className="flex-grow"
                    href={FwLinks.LearnMoreVariables}
                    target={"_blank"}
                    rel={"noopener noreferrer"}>
                    {Resources.VariablesPanelLearnMore}
                </Link>

                <ButtonGroup className="flex-grow justify-end">
                    <Button
                        key="cancel"
                        text={isDirty ? Resources.Cancel : Resources.Close}
                        disabled={this.props.isSaving}
                        onClick={this._onDismiss} />
                    <Button
                        key="save"
                        text={Resources.SaveButtonLabel}
                        primary={true}
                        disabled={this.props.isSaving || !isDirty}
                        onClick={this._onSave} />
                </ButtonGroup>
            </PanelFooter>
        );
    }

    private _renderEmptyScenario(): JSX.Element {
        return (
            <div className="empty-variables-view flex-column flex-grow flex-self-center">
                <div className="flex-column flex-grow flex-noshrink justify-center flex-center text-center">
                    <div className="circle flex-center flex-self-center">
                        <Icon className="empty-icon" iconName={"Variable"} />
                    </div>
                    <div className="empty-text">{Resources.VariablesPanelEmptyText}</div>
                    <Button
                        className="empty-new-button"
                        text={Resources.VariablesPanelNewVariable}
                        primary={true}
                        onClick={this._onNewFromEmptyScenario} />
                    <Link
                        href={FwLinks.LearnMoreVariables}
                        target={"_blank"}
                        rel={"noopener noreferrer"}>
                        {Resources.VariablesPanelLearnMore}
                    </Link>
                </div>
                <div className="flex-grow no-width"></div>
            </div>
        );
    }

    private _onNewFromEmptyScenario = () => {
        const componentElement = ReactDOM.findDOMNode(this) as Element;
        const defaultFocusElement = componentElement && componentElement.querySelector(".bolt-panel-focus-element") as HTMLElement;
        if (defaultFocusElement && defaultFocusElement.focus) {
            defaultFocusElement.focus();
        }
        this._newVariable();
    }

    private _editPanelDismissed = () => {
        this.setState({
            isEditPanelOpen: false,
            variableToEdit: undefined
        });
    };

    private _updateVariable = (variable: PipelineVariableInfo) => {
        const original = this._originalVariables.find(value => value.key === variable.key);
        variable.isDirty = this._isVariableDirty(variable, original);
        variable.isSecretDirty = this._isSecretDirty(variable, original);
        const index = this._getIndex(variable);
        if (index > -1) {
            this._modifiedVariables.splice(index, 1, variable);
        }
        else {
            this._modifiedVariables.splice(0, 0, variable);
        }
        this._editPanelDismissed();
        this._onVariablesUpdated();
    };

    private _setDeleted(variable: PipelineVariableInfo, isDeleted: boolean) {
        const update = { ...variable, isDeleted }
        const index = this._getIndex(variable);
        this._modifiedVariables.splice(index, 1, update);
        this._onVariablesUpdated();
    }

    private _isVariableDirty(variable: PipelineVariableInfo, original?: PipelineVariableInfo) {
        return !original
            || variable.name !== original.name
            || variable.value !== original.value
            || variable.isSecret !== original.isSecret
            || variable.allowOverride !== original.allowOverride;
    }

    private _isSecretDirty(variable: PipelineVariableInfo, original?: PipelineVariableInfo) {
        return !original || variable.isSecret !== original.isSecret;
    }

    private _rowClicked = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<PipelineVariableInfo>) => {
        const variable = { ...listRow.data };
        if (variable && !variable.isDeleted) {
            this._editVariable(variable);
        }
    }

    private _onSearchTextChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this._throttledSearch(value);
        this.setState({ searchText: value });
    }

    private _newVariable = () => {
        const key = String(this._modifiedVariables.length);
        const variable: PipelineVariableInfo = {
            key: key,
            name: "",
            value: "",
            isNew: true,
            isDeleted: false
        };
        this._editVariable(variable)
    }

    private _editVariable = (variable: PipelineVariableInfo) => {
        this.setState({
            isEditPanelOpen: true,
            variableToEdit: variable
        });
    }

    private _getIndex(variable: PipelineVariableInfo): number {
        return this._modifiedVariables.findIndex(value => value.key === variable.key);
    }

    private _getExistingVariables(): PipelineVariableInfo[] {
        const variables = this.props.variables || {};

        return Object.keys(variables || {})
            .map((name: string, index: number) => {
                const variable = variables[name];
                return {
                    key: String(index),
                    name: name,
                    value: variable.value,
                    isSecret: variable.isSecret,
                    allowOverride: variable.allowOverride,
                    isDeleted: false,
                    isNew: false,
                };
            })
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase(), navigator.language));
    }

    private _getUpdatedPipelineVariables(): { [name: string]: BuildDefinitionVariable } {
        const definitionVariables: { [key: string]: BuildDefinitionVariable } = {};
        this._modifiedVariables
            .filter(info => !info.isDeleted)
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase(), navigator.language))
            .forEach(info => definitionVariables[info.name] = {
                ...{
                    value: info.value!,
                    isSecret: info.isSecret!,
                    allowOverride: info.allowOverride!
                }
            });
        return definitionVariables;
    }

    private _onDismiss = () => {
        this.props.onDismiss();
    }

    private _onSave = () => {
        this.props.onSave(this._getUpdatedPipelineVariables());
    }

    private _onVariablesUpdated() {
        this._updateFilteredList(this.state.searchText);
        this._updateOverallDirtyState();
    }

    /** Fiters the variable list and sorts by exact match, then starts with, then contains. */
    private _updateFilteredList(searchText: string) {
        if (searchText) {
            searchText = searchText.toLocaleLowerCase();
            this._filteredVariables.value = this._modifiedVariables.filter(variable => {
                return variable.name.toLocaleLowerCase().indexOf(searchText) > -1
            }).sort((a, b) => {
                const al = a.name.toLocaleLowerCase();
                const bl = b.name.toLocaleLowerCase();
                const aOrder = al === searchText? 0 : al.startsWith(searchText) ? 1 : 2;
                const bOrder = bl === searchText? 0 : bl.startsWith(searchText) ? 1 : 2;
                return aOrder - bOrder;
            });
        }
        else {
            this._filteredVariables.value = [...this._modifiedVariables];
        }
    }

    private _updateOverallDirtyState() {
        const isDirty = !!this._modifiedVariables.find(variable => {
            if (variable.isNew) {
                return !variable.isDeleted;
            }
            else {
                return variable.isDirty || variable.isDeleted;
            }
        });

        if (this.state.isDirty !== isDirty) {
            this.setState({ isDirty });
        }
    }

    private _onNavigateAway = (ev: BeforeUnloadEvent | CustomEvent) => {
        if (this.state.isDirty) {
            showLoseChangesDialog(this.context.pageContext, ev);
        }
    }
}
